// Where a dropdown panel goes relative to the control that opened it. Kept
// pure so the edge cases (no room below, no room either way, a panel wider
// than the space to its right) are unit-tested rather than eyeballed.

/** Space between the control and the panel. */
export const PANEL_GAP = 4
/** Room always kept between the panel and the edge of the window. */
export const VIEWPORT_GUTTER = 8

export interface Rect {
  top: number
  bottom: number
  left: number
  right: number
}

export interface Placement {
  /** Which way the panel opens from the control. */
  side: 'below' | 'above'
  /** Distance from the top of the window to the panel's top edge (below), or
   *  from the bottom of the window to its bottom edge (above). */
  offset: number
  left: number
  /** The panel scrolls inside this height rather than running off screen. */
  maxHeight: number
}

export function placePanel(
  anchor: Rect,
  panel: { width: number; height: number },
  viewport: { width: number; height: number },
  align: 'start' | 'end' = 'start',
): Placement {
  const below = viewport.height - anchor.bottom - PANEL_GAP - VIEWPORT_GUTTER
  const above = anchor.top - PANEL_GAP - VIEWPORT_GUTTER
  // Below is the expected place, so it wins whenever the panel fits there, and
  // otherwise whenever it has at least as much room as above.
  const side = panel.height <= below || below >= above ? 'below' : 'above'
  const room = Math.max(0, side === 'below' ? below : above)

  const preferred = align === 'end' ? anchor.right - panel.width : anchor.left
  const rightmost = viewport.width - VIEWPORT_GUTTER - panel.width
  const left = Math.max(VIEWPORT_GUTTER, Math.min(preferred, rightmost))

  return {
    side,
    offset: side === 'below' ? anchor.bottom + PANEL_GAP : viewport.height - anchor.top + PANEL_GAP,
    left,
    maxHeight: room,
  }
}
